import { useState } from 'react';
import { clear } from 'idb-keyval';
import { useApp } from '../state/store';
import { Button, Chip, Icon } from '../ui';
import { resetTours } from '../ui/Tour';

const SECTIONS = [
  { id: 'how', label: 'How it works' },
  { id: 'veo', label: 'Getting Veo timestamps' },
  { id: 'lists', label: 'Other lists' },
  { id: 'kickoffs', label: 'Syncing the kickoffs' },
  { id: 'live', label: 'Marking live' },
  { id: 'clips', label: 'Reviewing clips' },
  { id: 'export', label: 'Exporting' },
  { id: 'keys', label: 'Keyboard shortcuts' },
  { id: 'privacy', label: 'Privacy and your data' },
  { id: 'faq', label: 'Problems and questions' },
];

export function GuideScreen() {
  const { closeGuide, replayTour, setStep, file, step } = useApp();
  const [cleared, setCleared] = useState(false);
  const backLabel = { start: 'Back to start', timestamps: 'Back to timestamps', sync: 'Back to kickoffs', mark: 'Back to marking', clips: 'Back to clips', export: 'Back to export' }[step];
  const go = (id: string) => document.getElementById(`guide-${id}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });

  return (
    <div className="guide">
      <nav className="guide-toc" aria-label="Guide sections">
        <div style={{ paddingBottom: 12 }}>
          <Button icon="arrow-left" onClick={closeGuide}>
            {file ? backLabel : 'Back to the app'}
          </Button>
        </div>
        {SECTIONS.map((s) => (
          <button key={s.id} type="button" onClick={() => go(s.id)}>
            {s.label}
          </button>
        ))}
        <div style={{ paddingTop: 12 }}>
          <Button
            variant="ghost"
            icon="play"
            onClick={() => {
              resetTours();
              closeGuide();
              if (!file) setStep('start');
              replayTour();
            }}
          >
            Replay the tour
          </Button>
        </div>
      </nav>

      <article className="guide-body">
        <section id="guide-how" aria-labelledby="h-how">
          <span className="app-label">Guide</span>
          <h1 id="h-how">From full match to highlight reel.</h1>
          <p>Highlights cuts the moments you care about out of a full match video and joins them into one MP4. Everything happens in your browser. The video is never uploaded.</p>
          <ol className="guide-steps">
            <li>
              <span className="num">01</span>
              <strong>Pick the match video</strong>
              <span className="text-small muted">The whole recording, straight from Veo or your camera. Several GB is fine.</span>
            </li>
            <li>
              <span className="num">02</span>
              <strong>Find the highlights</strong>
              <span className="text-small muted">Paste a timestamp list, or play the match and mark each highlight live.</span>
            </li>
            <li>
              <span className="num">03</span>
              <strong>Review the clips</strong>
              <span className="text-small muted">Preview, leave out, trim by a second, add clips you missed.</span>
            </li>
            <li>
              <span className="num">04</span>
              <strong>Export</strong>
              <span className="text-small muted">Horizontal, vertical or square. The file is saved where you choose.</span>
            </li>
          </ol>
          <p>
            A list of timestamps uses the <strong>match clock</strong>, so there is one extra step: you show the app where each half kicks off in your video. Marking live skips that step because your marks are already in video time.
          </p>
        </section>

        <section id="guide-veo" aria-labelledby="h-veo">
          <h2 id="h-veo">Getting Veo timestamps</h2>
          <ol>
            <li>On Veo, open the match and the player's highlight list.</li>
            <li>Expand both periods so every moment is visible.</li>
            <li>Select the whole list, copy it, and paste it into the timestamps box as it is.</li>
          </ol>
          <p>A pasted Veo list looks like this. Each line is a start time on the match clock followed by the length:</p>
          <pre className="guide-pre">{'1st period\n02:5914s     starts at 02:59, lasts 14 s\n04:0019s\n2nd period\n50:1212s'}</pre>
          <p>Player names, labels and empty lines are ignored. Duplicates are removed. The count per period is shown as you paste: check it against the number of highlights Veo shows.</p>
          <p className="text-small">Highlights works with Veo timestamps but isn't made by or affiliated with Veo.</p>
        </section>

        <section id="guide-lists" aria-labelledby="h-lists">
          <h2 id="h-lists">Other lists</h2>
          <p>Choose <strong>Paste any list</strong> for notes from a coach, a parent or yourself. One moment per line:</p>
          <table className="guide-table">
            <thead>
              <tr>
                <th>You write</th>
                <th>It means</th>
              </tr>
            </thead>
            <tbody>
              <tr><td><code>23:41</code></td><td>A moment at 23:41, using the default clip length (10 s unless you change it).</td></tr>
              <tr><td><code>23:41 20s Goal</code></td><td>20 s from 23:41, labelled “Goal”.</td></tr>
              <tr><td><code>31:18-31:40</code></td><td>Exactly from 31:18 to 31:40.</td></tr>
              <tr><td><code>45+2' corner</code></td><td>Added time: 47:00.</td></tr>
              <tr><td><code>1:02:15</code></td><td>Hours, minutes and seconds.</td></tr>
              <tr><td><code>2nd half</code></td><td>The lines below belong to the second half. “1st period”, “Second half” and “Extra time” work too.</td></tr>
            </tbody>
          </table>
          <p>
            Set <strong>Times in the list are</strong> to <strong>Match clock</strong> for times read off the match (the usual case) or <strong>Video time</strong> for times read off your video player. Video time skips the kickoff step.
          </p>
          <p>Lines without an exact time, like “around 70 min”, are shown as skipped with the reason, never silently dropped.</p>
        </section>

        <section id="guide-kickoffs" aria-labelledby="h-kickoffs">
          <h2 id="h-kickoffs">Syncing the kickoffs</h2>
          <p>Your recording usually includes the warm-up and half-time, so match clock and video time don't line up. The app works out each clip's place in the video like this:</p>
          <pre className="guide-pre">video time = kickoff in the video + (match clock − clock at kickoff)</pre>
          <ol>
            <li>Scrub to the moment the ball is first played in the 1st half and press <strong>Set</strong>.</li>
            <li>Do the same for the 2nd half (and extra time, if the list has it).</li>
            <li>Press <strong>Check first clip</strong> for each half and watch the first highlight play.</li>
          </ol>
          <h3>The first clip plays early or late</h3>
          <p>
            If every clip of one half is off by the same amount, that half's kickoff or clock is off. Veo's clock in the 2nd period doesn't always restart at exactly 45:00. For example, if the first half ran long, it may start at 47:17. Change <strong>Clock at 2nd kickoff</strong> until the check lines up.
          </p>
          <h3>Youth games and extra time</h3>
          <p>Pick the game length (2 × 45, 40, 35, 30 or 25 min) to set the usual clock values. Extra-time periods get their own kickoff when the list contains them.</p>
        </section>

        <section id="guide-live" aria-labelledby="h-live">
          <h2 id="h-live">Marking live</h2>
          <p>
            Choose <strong>Mark it live</strong> at the start when you don't have a list. Play the match and press <strong>M</strong> (or the Mark button):
          </p>
          <ul>
            <li><strong>Start, then end</strong>: press where the highlight starts and again where it ends. Esc cancels a clip you started.</li>
            <li><strong>One press</strong>: each press covers a set window, by default 8 s before and 2 s after, because you usually react just after the moment.</li>
          </ul>
          <p>Watch at 2× or 4× and slow down when something happens. Each clip goes straight into your reel, and you can still trim it on the next step.</p>
        </section>

        <section id="guide-clips" aria-labelledby="h-clips">
          <h2 id="h-clips">Reviewing clips</h2>
          <ul>
            <li>Click a clip to jump to it, <strong>P</strong> to play just that clip.</li>
            <li>Untick a clip (or press <strong>X</strong>) to leave it out without losing it.</li>
            <li><strong>−1s / +1s</strong> move the start or end by a second. <strong>Set start</strong> and <strong>Set end</strong> trim the selected clip to the playhead.</li>
            <li><strong>Add clip here</strong> (or <strong>A</strong>) adds a 10 s clip ending 2 s after the playhead, for moments the list missed.</li>
            <li>Give a clip a label if you like. Labels show in the list and the export progress.</li>
          </ul>
          <p>
            Each list clip gets padding before and after (1 s by default, set on the export step). Clips that overlap or sit less than the merge gap apart are joined into one, marked <Chip>2 timestamps merged</Chip>.
          </p>
        </section>

        <section id="guide-export" aria-labelledby="h-export">
          <h2 id="h-export">Exporting</h2>
          <table className="guide-table">
            <thead>
              <tr>
                <th>Format</th>
                <th>Good for</th>
              </tr>
            </thead>
            <tbody>
              <tr><td>Horizontal 16:9</td><td>YouTube, TV, laptops. Keeps the full picture.</td></tr>
              <tr><td>Vertical 9:16</td><td>Reels, TikTok, Shorts, WhatsApp status. Crops a slice of the frame or fits it with blurred bars.</td></tr>
              <tr><td>Square 1:1</td><td>Instagram and other feed posts.</td></tr>
            </tbody>
          </table>
          <p>
            <strong>Crop position</strong> sets which part of the frame a vertical or square crop keeps, for the whole reel. Veo footage already follows the play, so the centre usually works. <strong>720p</strong> makes a much smaller file for messaging apps.
          </p>
          <p>
            In Chrome and Edge you choose where to save before the export starts, and the file is written straight to disk as it's made. A long reel takes a few minutes on a recent laptop. Keep the tab open; the screen stays awake until it's done. You can cancel at any time, and nothing half-finished is kept.
          </p>
        </section>

        <section id="guide-keys" aria-labelledby="h-keys">
          <h2 id="h-keys">Keyboard shortcuts</h2>
          <table className="guide-table">
            <thead>
              <tr>
                <th>Key</th>
                <th>Where</th>
                <th>Does</th>
              </tr>
            </thead>
            <tbody>
              <tr><td><code>Space</code></td><td>Any player</td><td>Play or pause</td></tr>
              <tr><td><code>←</code> <code>→</code></td><td>Any player</td><td>Step 1 s (Shift: 10 s)</td></tr>
              <tr><td><code>M</code></td><td>Mark live</td><td>Start or end a highlight</td></tr>
              <tr><td><code>Esc</code></td><td>Mark live</td><td>Cancel the clip being recorded</td></tr>
              <tr><td><code>↑</code> <code>↓</code></td><td>Clips</td><td>Previous or next clip</td></tr>
              <tr><td><code>P</code></td><td>Clips</td><td>Play the selected clip</td></tr>
              <tr><td><code>X</code></td><td>Clips</td><td>Leave out or include</td></tr>
              <tr><td><code>A</code></td><td>Clips</td><td>Add a clip at the playhead</td></tr>
              <tr><td><code>?</code></td><td>Anywhere</td><td>Open this guide</td></tr>
            </tbody>
          </table>
        </section>

        <section id="guide-privacy" aria-labelledby="h-privacy">
          <h2 id="h-privacy">Privacy and your data</h2>
          <ul>
            <li>Your video is read and encoded in this browser. It is never uploaded, not even a thumbnail.</li>
            <li>Your project (list, kickoffs, clip edits and settings) is saved in this browser only, so you can close the tab and continue later by opening the same video file.</li>
            <li>There are no accounts and no cookies. Fonts are served from this site, not from Google.</li>
          </ul>
          <div className="app-row" style={{ flexWrap: 'wrap' }}>
            <Button
              icon="trash"
              onClick={async () => {
                await clear().catch(() => undefined);
                setCleared(true);
              }}
            >
              Forget saved projects
            </Button>
            {cleared && (
              <span className="app-row text-small" style={{ gap: 6, color: 'var(--pitch)' }}>
                <Icon name="check" size={16} />
                Saved projects removed from this browser.
              </span>
            )}
          </div>
        </section>

        <section id="guide-faq" aria-labelledby="h-faq" className="guide-faq">
          <h2 id="h-faq">Problems and questions</h2>
          <details>
            <summary>It says this browser cannot encode video.</summary>
            <p>Use Chrome or Edge on a laptop or desktop. They have the video encoder the app needs. Firefox and Safari may not.</p>
          </details>
          <details>
            <summary>Fewer timestamps than Veo shows.</summary>
            <p>Part of the list wasn't copied. Make sure both periods are expanded on Veo, copy the whole list again and paste it over the old one.</p>
          </details>
          <details>
            <summary>All clips of one half start too early or too late.</summary>
            <p>That half's kickoff or its clock at kickoff is off. Go back to Kickoffs, adjust, and use Check first clip. See Syncing the kickoffs above.</p>
          </details>
          <details>
            <summary>The export downloads at the end instead of asking where to save.</summary>
            <p>Your browser can't write straight to disk, so the reel is built in memory. Long reels may not fit. Choose 720p, or use Chrome or Edge.</p>
          </details>
          <details>
            <summary>The file won't open or play.</summary>
            <p>The app reads MP4 and MOV with H.264 video, which is what Veo and most cameras export. Other formats may need converting first.</p>
          </details>
          <details>
            <summary>My project is gone.</summary>
            <p>Projects are tied to the exact video file (its name, size and date) and to this browser. Open the same file in the same browser to restore it. Private windows don't keep projects.</p>
          </details>
          <details>
            <summary>Is the vertical version lower quality?</summary>
            <p>A vertical crop uses a 608 px wide slice of a 1080p video, scaled up. It looks fine on phones but softer than the horizontal version. “Fit with blurred bars” keeps the whole picture instead.</p>
          </details>
        </section>
      </article>
    </div>
  );
}
